import React from 'react';
import { Box, Typography } from '@mui/material';
import { Phone } from 'lucide-react';
import { CallType, CallMode } from '../../routes/agent/RolePlay';
import { scriptRegistry } from '../../config/scripts';

interface ModernIncomingCallProps {
  agentInfo: any;
  callType: CallType;
  callMode?: CallMode;
  scriptId?: string;
  onAnswer: () => void;
  onDecline: () => void;
}

export const ModernIncomingCall: React.FC<ModernIncomingCallProps> = ({
  agentInfo,
  callType,
  callMode,
  scriptId,
  onAnswer,
  onDecline,
}) => {
  const isScriptedMode = callMode === CallMode.SCRIPTED;
  const scriptMetadata = scriptId ? scriptRegistry[scriptId]?.metadata : null;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 500,
        textAlign: 'center',
      }}
    >
      {/* Avatar with pulse animation */}
      <Box
        sx={{
          position: 'relative',
          width: 180,
          height: 180,
          margin: '0 auto',
          mb: 4,
        }}
      >
        {/* Pulse rings */}
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'primary.main',
              opacity: 0.3,
              animation: 'pulse 3s infinite',
              animationDelay: `${index * 0.5}s`,
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 0.3,
                },
                '50%': {
                  transform: 'scale(1.1)',
                  opacity: 0.1,
                },
                '100%': {
                  transform: 'scale(1.2)',
                  opacity: 0,
                },
              },
            }}
          />
        ))}

        {/* Avatar */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            backgroundColor: 'white',
          }}
        >
          <img
            src={agentInfo?.profile?.avatar || 'https://dopmo1eihgbgm.cloudfront.net/67135d6f890cc59eb99e65f9/ai-headshot.webp'}
            alt={agentInfo?.profile?.name || 'Rachel'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center top',
            }}
          />
        </Box>
      </Box>

      {/* Agent Info */}
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#333' }}>
        {agentInfo?.profile?.name || 'Rachel'}
      </Typography>

      <Typography variant="body1" sx={{ color: '#666', mb: 1 }}>
        {agentInfo?.profile?.company || 'Prudential Singapore'}
      </Typography>

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.5,
          bgcolor: isScriptedMode ? '#FFF3E0' : '#E3F2FD',
          color: isScriptedMode ? '#E65100' : '#1565C0',
          borderRadius: 20,
          fontSize: '14px',
          fontWeight: 500,
          mb: 4,
        }}
      >
        {isScriptedMode ? (scriptMetadata?.title || 'Annual Policy Review') : 'Insurance Consultation'}
      </Box>

      {/* Call Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 8,
          mt: 6,
        }}
      >
        {/* Decline Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Box
            onClick={onDecline}
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#F5F5F5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                bgcolor: '#E0E0E0',
                transform: 'scale(1.05)',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <Phone
              size={32}
              style={{
                transform: 'rotate(135deg)',
                color: '#666',
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ mt: 2, color: '#666', fontWeight: 500 }}
          >
            Decline
          </Typography>
        </Box>

        {/* Answer Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Box
            onClick={onAnswer}
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#4CAF50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
              animation: 'phoneRing 1s ease-in-out infinite',
              '@keyframes phoneRing': {
                '0%, 100%': {
                  transform: 'rotate(0deg)',
                },
                '10%, 30%': {
                  transform: 'rotate(-10deg)',
                },
                '20%, 40%': {
                  transform: 'rotate(10deg)',
                },
              },
              '&:hover': {
                bgcolor: '#45A049',
                transform: 'scale(1.05)',
                animation: 'none',
              },
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <Phone size={32} color="white" />
          </Box>
          <Typography
            variant="body2"
            sx={{ mt: 2, color: '#333', fontWeight: 600 }}
          >
            Answer
          </Typography>
        </Box>
      </Box>

    </Box>
  );
};